/**
 * The chart's gesture policy (GRA-130).
 *
 * The page must always scroll under the player's default gesture, even with
 * the pointer or finger over the chart: on a phone the chart is most of the
 * viewport, so a chart that swallows the natural scroll gesture makes
 * everything below it (the controls, the result, the history) unreachable.
 *
 * The policy, in one line each:
 *
 * - Wheel / trackpad, vertical: scrolls the PAGE. Never zooms.
 * - Wheel / trackpad, vertical + Ctrl (or ⌘, or a trackpad pinch, which the
 *   browser reports as a Ctrl-wheel): zooms the CHART.
 * - Wheel / trackpad, horizontal: pans the CHART through time. The page has no
 *   horizontal scroll to steal, so this needs no modifier.
 * - Touch, vertical swipe: scrolls the PAGE (`touch-action: pan-y` on the
 *   chart container hands vertical panning to the browser).
 * - Touch, horizontal drag: pans the CHART. Pinch zooms the CHART, since
 *   `pan-y` reserves nothing but vertical panning for the browser.
 * - Touch, any drag while a drawing tool is armed: draws (`touch-action: none`
 *   for as long as the tool is armed, so the stroke isn't stolen by a scroll).
 *
 * Why this is enforced from the outside rather than with KLineChart's own
 * switches: `setZoomEnabled(false)` stops the zoom but not the interception.
 * The library subscribes to `wheel` with `{ passive: false }` and calls
 * `preventDefault()` on every vertical wheel event before it consults any
 * enable flag, so the page stays frozen whatever the flags say. The one place
 * to intervene is therefore before that listener runs: a capture-phase
 * listener on the container that stops the event from reaching the library
 * when the gesture belongs to the page. Nothing is prevented and no overlay
 * element sits over the canvas, so pointer events (crosshair, drawing tools,
 * overlay drags) are untouched.
 */

/** What a wheel event over the chart is asking for. */
export type WheelIntent =
  /** Let the browser scroll the page; keep the event away from the chart. */
  | "page"
  /** Hand the event to the chart (zoom on vertical, pan on horizontal). */
  | "chart";

/** The fields of a WheelEvent the policy reads (kept narrow so it is testable
 *  with a plain object, and so no DOM is needed to decide). */
export type WheelGesture = {
  deltaX: number;
  deltaY: number;
  ctrlKey: boolean;
  metaKey: boolean;
};

/**
 * Decide who owns a wheel event over the chart. Horizontal-dominant wheels go
 * to the chart because the page never scrolls sideways here, so nothing is
 * taken from the player. Vertical wheels go to the page unless Ctrl/⌘ is held:
 * that is the deliberate "I mean the chart" gesture, and it is also what a
 * trackpad pinch arrives as, so pinch-to-zoom works on a laptop with no
 * keyboard involved.
 */
export function wheelIntent(e: WheelGesture): WheelIntent {
  if (e.ctrlKey || e.metaKey) return "chart";
  if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return "chart";
  return "page";
}

/**
 * The CSS `touch-action` the chart container should carry. `pan-y` gives the
 * browser vertical page scrolling and leaves horizontal drags and pinches to
 * the chart; while a drawing tool is armed the whole gesture belongs to the
 * drawing, so nothing is reserved for the browser.
 */
export function chartTouchAction(drawingArmed: boolean): "none" | "pan-y" {
  return drawingArmed ? "none" : "pan-y";
}
